import { useMemo } from 'react'
import { beginTurn, markTurnStreaming, endTurn, updateTurnChatId, isTurnLive } from '../store'
import { applySinkPatches } from '../store/messages'
import { createCoalescingSink, rafScheduler } from '../engine/coalescing-sink'
import type { EngineDeps } from '../engine/services'
import { reportTurnError, traceTurnEvent } from '../engine/telemetry'
import { useChatApi } from './use-chat-api'

/**
 * The one process-wide sink: every facade's patches share a buffer, so a
 * `flush()` anywhere (turn settle, the temp→real migration) drains everything
 * ordered ahead of it — no cross-instance interleaving to reason about.
 */
const sink = createCoalescingSink(applySinkPatches, rafScheduler)

/**
 * The engine's services, bound to the real store, api, and telemetry. Shared
 * by the resume (`use-subscribe-to-chat`) and submit (`useSubmitMessage`)
 * facades. The sink is the single ordered writer for the engine's store
 * patches, rAF-coalesced (HIG-5964): events buffer per frame and
 * `applySinkPatches` folds each frame's buffer into one store write. Patch
 * semantics — including assistant adds adopting an unadopted optimistic
 * placeholder — live in `store/sink-patches.ts`.
 */
export function useEngineDeps(): EngineDeps {
  const api = useChatApi()

  return useMemo<EngineDeps>(
    () => ({
      api,
      sink,
      streaming: {
        begin: (chatId, title) => beginTurn(chatId, { title }),
        markStreaming: markTurnStreaming,
        end: endTurn,
        updateChatId: updateTurnChatId,
        isActive: isTurnLive,
      },
      telemetry: {
        report: reportTurnError,
        trace: traceTurnEvent,
      },
    }),
    [api],
  )
}
